import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Link,
  Paper,
  useTheme,
  Divider,
  CircularProgress,
} from '@mui/material';
import axios from '../config/axiosConfig';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EtfInformationDoughnutChart from './EtfInformationDoughnutChart';
import EtfHoldingsComponent from './EtfHoldings';

const CompanyProfile = ({ data }) => {
  const theme = useTheme();
  const companyData = data?.[0];
  const [etfHoldings, setEtfHoldings] = useState(null);
  const [etfInformation, setEtfInformation] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchEtfData = async () => {
      try {
        if (companyData?.isEtf && companyData?.symbol) {
          const holdingsResponse = await axios.get(`/api/etf-holdings/${companyData.symbol}`);
          if (isMounted && holdingsResponse.data) setEtfHoldings(holdingsResponse.data.data);
          const infoResponse = await axios.get(`/api/etf-information/${companyData.symbol}`);
          if (isMounted && infoResponse.data) setEtfInformation(infoResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching ETF data:', error.response?.data || error.message);
        if (isMounted) { setEtfHoldings(null); setEtfInformation(null); }
      }
    };
    fetchEtfData();
    return () => { isMounted = false; };
  }, [companyData?.isEtf, companyData?.symbol]);

  const hasValue = (val) => {
    if (!val && val !== 0) return false;
    if (typeof val !== 'string') return true;
    const trimmed = val.trim().toUpperCase();
    return !['NA', 'N/A', 'NOT AVAILABLE', 'NOT APPLICABLE', ''].includes(trimmed);
  };

  const formatAddress = (data) =>
    [data.address, data.city, data.state, data.zip].filter(hasValue).join(', ') || null;

  if (!companyData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" width="100%">
        <Typography variant="h6" color="text.secondary">No company profile data available</Typography>
      </Box>
    );
  }

  const website = companyData.website || etfInformation?.website;
  const address = formatAddress(companyData);

  const sectorToSlug = (sector) => {
    if (!sector) return null;
    const map = {
      'Energy': 'energy',
      'Financial Services': 'financial-services',
      'Healthcare': 'healthcare',
      'Industrials': 'industrials',
      'Materials': 'materials',
      'Consumer Staples': 'consumer-staples',
      'Consumer Discretionary': 'consumer-discretionary',
      'Communication Services': 'communication-services',
      'Real Estate': 'real-estate',
      'Technology': 'technology',
      'Utilities': 'utilities',
    };
    return map[sector] || sector.toLowerCase().replace(/\s+/g, '-');
  };

  // Info rows for the right card
  const infoRows = [
    { label: 'CEO', value: companyData.ceo },
    { label: 'Country', value: companyData.country },
    { label: 'IPO Date', value: companyData.ipoDate ? new Date(companyData.ipoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null },
    { label: 'Industry', value: companyData.industry },
    { label: 'Employees', value: companyData.fullTimeEmployees ? parseInt(companyData.fullTimeEmployees.toString().replace(/,/g, '')).toLocaleString() : null },
  ].filter(r => hasValue(r.value));

  const contactRows = [
    { label: 'Address', value: address },
    { label: 'Phone', value: companyData.phone },
  ].filter(r => hasValue(r.value));

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
      <Grid container spacing={3}>

        {/* ── Left: Description ── */}
        <Grid item xs={12} md={8}>
          {hasValue(companyData.description) ? (
            <Typography variant="body2" sx={{ lineHeight: 1.8, color: theme.palette.text.secondary }}>
              {companyData.description}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No company description available.
            </Typography>
          )}
        </Grid>

        {/* ── Right: Info Card ── */}
        <Grid item xs={12} md={4}>
          {/* Company Info Card */}
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Company Info
              </Typography>
            </Box>
            {infoRows.map((row, i) => (
              <Box key={row.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 2, py: 1.25, gap: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.75rem', textAlign: 'right' }}>
                    {row.value}
                  </Typography>
                </Box>
                {i < infoRows.length - 1 && <Divider />}
              </Box>
            ))}
            {hasValue(companyData.sector) && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, gap: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}>
                    Sector
                  </Typography>
                  <Link
                    href={`/lists/sector/${sectorToSlug(companyData.sector)}?exchanges=NASDAQ%2CNYSE%2CAMEX`}
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textAlign: 'right',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {companyData.sector}
                  </Link>
                </Box>
              </>
            )}
          </Paper>

          {/* Contact Details Card */}
          {(contactRows.length > 0 || hasValue(website)) && (
            <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Contact Details
                </Typography>
              </Box>
              {contactRows.map((row, i) => (
                <Box key={row.label}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 2, py: 1.25, gap: 2 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.75rem', textAlign: 'right' }}>
                      {row.value}
                    </Typography>
                  </Box>
                  {(i < contactRows.length - 1 || hasValue(website)) && <Divider />}
                </Box>
              ))}
              {hasValue(website) && (
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Link
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: theme.palette.primary.main, textDecoration: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                  >
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem' }}>
                      Website
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      <OpenInNewIcon sx={{ fontSize: 13 }} />
                    </Box>
                  </Link>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* ── ETF Sector Allocation Chart ── */}
        {companyData.isEtf && (
          <Grid item xs={12}>
            {etfInformation === null ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3} minHeight={200}>
                <CircularProgress />
              </Box>
            ) : etfInformation?.sectorsList?.length > 0 ? (
              <EtfInformationDoughnutChart data={etfInformation} />
            ) : null}
          </Grid>
        )}

        {/* ── ETF Holdings Table ── */}
        {companyData.isEtf && (
          <Grid item xs={12}>
            {etfHoldings === null ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3} minHeight={200}>
                <CircularProgress />
              </Box>
            ) : etfHoldings?.holdings?.length > 0 ? (
              <EtfHoldingsComponent data={etfHoldings} />
            ) : null}
          </Grid>
        )}

      </Grid>
    </Box>
  );
};

export default CompanyProfile;